import { isAction, Middleware } from '@reduxjs/toolkit'

import slice2D, {
    addEstimatedOptionOverlayChunkId,
    addOverlayChunkId,
    createDrawableLocation,
    removeEstimatedOptionOverlayChunkId,
    removeOverlayChunkId,
    resetEstimatedOptionOverlayChunkId,
    setEstimatedOptionOverlayChunkId,
    setOverlayChunkId,
    State2D,
    updateOverlayChunkId,
} from '../slices/2D'
import commonSlice, { availableMode, enableIMUP } from '../slices/common'
import type { GeometryState } from '../slices/geometry'
import masterSetPlanSlice, {
    MaterialInteractionState,
    selectAreBaseHomeMaterialsReadOnly,
    selectIsMaterialGroupActivationBlocked,
    updateConflictResolutionFlag,
    updateOverlaidChunkLockedFlag,
    updateReviewingConflictsFlag,
} from '../slices/masterSetPlan'
import projectSlice, { gotProjectAction } from '../slices/project'
import {
    SET_ACTIVE_DRAWABLE_GROUP,
    SET_ACTIVE_DRAWABLE_GROUP_FROM_ID,
    setActiveDrawableGroup,
    setActiveDrawableGroupFromID,
    TOGGLE_ACTIVE_DRAWABLE,
    toggleActiveDrawable,
} from '../../actions/drawable'

type ConflictSelectionState = MaterialInteractionState & {
    IMUP: {
        '2D': State2D
        geometry: Pick<GeometryState, 'activeGeometryGroupID'>
        common: ReturnType<typeof commonSlice.reducer>
        project: ReturnType<typeof projectSlice.reducer>
    }
}

// Prevent group activation before actions reach reducers and form/canvas sagas.
// Conflict paths use geometry selection independently of material groups.
export const conflictSelectionMiddleware: Middleware<{}, ConflictSelectionState> =
    ({ getState, dispatch }) =>
    (next) =>
    (action) => {
        if (!isAction(action)) return next(action)

        const state = getState()
        const isBlocked = selectIsMaterialGroupActivationBlocked(state)

        if (isBlocked) {
            if (
                createDrawableLocation.match(action) ||
                action.type === SET_ACTIVE_DRAWABLE_GROUP_FROM_ID ||
                action.type === TOGGLE_ACTIVE_DRAWABLE ||
                (action.type === SET_ACTIVE_DRAWABLE_GROUP &&
                    !Array.isArray((action as ReturnType<typeof setActiveDrawableGroup>).payload.activeDrawableGroup))
            ) {
                return action
            }
        }

        if (
            selectAreBaseHomeMaterialsReadOnly(state) &&
            (action.type === SET_ACTIVE_DRAWABLE_GROUP_FROM_ID ||
                action.type === TOGGLE_ACTIVE_DRAWABLE ||
                action.type === SET_ACTIVE_DRAWABLE_GROUP)
        ) {
            const optionChunkIds = new Set([
                ...state.IMUP['2D'].currentEstimatedOptionOverlayChunkIds,
                ...state.IMUP['2D'].currentOverlayChunkIds,
            ])
            const editableLocations = state.IMUP['2D'].drawableLocations.filter((location) =>
                optionChunkIds.has(location.document_chunk_id)
            )
            const editableDrawableIds = new Set(editableLocations.map((location) => location.drawable_id))

            if (action.type === SET_ACTIVE_DRAWABLE_GROUP_FROM_ID) {
                const { payload } = action as ReturnType<typeof setActiveDrawableGroupFromID>

                if (
                    !editableLocations.some(
                        (location) =>
                            location.drawable_id === payload.activeDrawableId &&
                            location.opening_location_id === payload.activeOpeningLocationId
                    )
                ) {
                    return action
                }
            }

            if (action.type === TOGGLE_ACTIVE_DRAWABLE) {
                const { drawable_id } = (action as ReturnType<typeof toggleActiveDrawable>).payload
                const ids = Array.isArray(drawable_id) ? drawable_id : [drawable_id]

                if (ids.some((id) => !editableDrawableIds.has(id))) return action
            }

            if (action.type === SET_ACTIVE_DRAWABLE_GROUP) {
                const { activeDrawableGroup, activeDrawableId } = (action as ReturnType<typeof setActiveDrawableGroup>)
                    .payload

                if (!Array.isArray(activeDrawableGroup)) {
                    const selectedIds =
                        activeDrawableId ?? activeDrawableGroup.openings.map((opening: { id: number }) => opening.id)
                    const ids: number[] = Array.isArray(selectedIds) ? selectedIds : [selectedIds]

                    // A newly created group has no openings until the first shape is saved.
                    if (ids.some((id) => !editableDrawableIds.has(id))) return action
                }
            }
        }

        if (
            updateConflictResolutionFlag.match(action) ||
            updateOverlaidChunkLockedFlag.match(action) ||
            updateReviewingConflictsFlag.match(action) ||
            enableIMUP.match(action) ||
            availableMode.match(action) ||
            gotProjectAction.match(action) ||
            setOverlayChunkId.match(action) ||
            addOverlayChunkId.match(action) ||
            removeOverlayChunkId.match(action) ||
            updateOverlayChunkId.match(action) ||
            setEstimatedOptionOverlayChunkId.match(action) ||
            addEstimatedOptionOverlayChunkId.match(action) ||
            resetEstimatedOptionOverlayChunkId.match(action) ||
            removeEstimatedOptionOverlayChunkId.match(action)
        ) {
            const nextState = {
                IMUP: {
                    ...state.IMUP,
                    common: commonSlice.reducer(state.IMUP.common, action),
                    project: projectSlice.reducer(state.IMUP.project, action),
                    masterSetPlan: masterSetPlanSlice.reducer(state.IMUP.masterSetPlan, action),
                    '2D': slice2D.reducer(state.IMUP['2D'], action),
                },
            }

            if (
                (selectIsMaterialGroupActivationBlocked(nextState) ||
                    (selectAreBaseHomeMaterialsReadOnly(nextState) && !selectAreBaseHomeMaterialsReadOnly(state))) &&
                state.IMUP.geometry.activeGeometryGroupID !== null
            ) {
                // Clear canvas selection and group locks before material interaction is disabled.
                dispatch(setActiveDrawableGroup(null))
            }
        }

        return next(action)
    }
